import { languages } from '../../../../../../shared/app/constants/languages';
import { assessmentResultsServiceModule } from '../../../../services/assessment-results-service/assessment-results-service';
import { solutionsServiceModule } from '../../../../services/solutions-service/solutions-service';
import { firebaseServiceModule } from '../../../../../../shared/app/services/firebase/firebase';
import { runnerFrameComponentModule } from '../../../../../../shared/app/components/runner-frame/runner-frame';
import { appAlertComponentModule } from '../../../../../../shared/app/components/app-alert/app-alert';
import { challengeProcessHelperModule } from '../challenge-process-helper';
import { ucFirst } from '../../../../../../shared/app/utils/string-utils';

export const codeChallengeProcessServiceModule = angular
  .module(
    'Q.routes.assessments.challenges.code-challenge.code-challenge-process-service',
    [
      assessmentResultsServiceModule,
      solutionsServiceModule,
      firebaseServiceModule,
      runnerFrameComponentModule,
      appAlertComponentModule,
      challengeProcessHelperModule,
    ],
  )

  .factory(
    'codeChallengeProcessService',
    function (
      solutionsService,
      AssessmentResult,
      Solution,
      $q,
      firebase,
      runnerFrameService,
      appAlert,
      $log,
      assessmentResultsService,
      challengeProcessHelper,
    ) {
      const _languageHistories = {};
      // string values correspond to the reset routes
      let _SUBMISSION_PART = {
        TEST_CASES: 'TestCases',
        SOLUTION: 'Solution',
        ALL: '',
      };

      let _lastLanguageName = null;

      return {
        create,
      };

      //// functions:

      function _mapHistory(from, to = {}) {
        [
          'code',
          'hiddenPassedTestCases',
          'output',
          'passedTestCases',
          'response',
          'score',
          'testCases',
          'testFramework',
          'totalTestCases',
          'wallTime',
          'weightedPassedTestCases',
        ].forEach((k) => {
          to[k] = from[k];
        });
        return to;
      }

      function _updateFromLanguageHistory(solution, language) {
        if (!_languageHistories[solution.id]) {
          _languageHistories[solution.id] = {};
        }
        let history = _languageHistories[solution.id];

        if (solution.language) {
          history[solution.language] = _mapHistory(solution);
        }

        solution.language = language;

        let previous = history[language];
        // if we have some history then lets use it
        if (previous) {
          _mapHistory(previous, solution);
          return true;
        } else {
          return false;
        }
      }

      function create(challenge, assessmentResultProcess) {
        const assessment = assessmentResultProcess.assessment;
        const assessmentResult = assessmentResultProcess.assessmentResult;
        let process = {
          language: null,
          challenge,
          assessmentResult,
          challengeLanguage: null,
          beginChallenge,
          switchToLanguage,
          reset,
          resetSolution: reset.bind(null, _SUBMISSION_PART.SOLUTION),
          resetTestCases: reset.bind(null, _SUBMISSION_PART.TEST_CASES),
          resetAll: reset.bind(null, _SUBMISSION_PART.ALL),
          submitSolution,
          runTests,
          setResponse,
        };

        return challengeProcessHelper(process, assessmentResultProcess).then(
          (process) => {
            if (process.solution) {
              switchToLanguage(process.solution.language);
            } else {
              const challengeItem =
                assessmentResult.getChallengeItem(challenge);
              const langNameEnabled = (lName) =>
                languages[lName] &&
                !challengeItem.disabledLanguages.includes(lName);
              const defaultLanguageName = assessment.defaultLanguage;
              const defaultLanguage = challenge.languages.find(
                (l) => l.name === defaultLanguageName,
              );
              const lastLanguage =
                _lastLanguageName &&
                challenge.languages.find((l) => l.name === _lastLanguageName);

              if (defaultLanguage && langNameEnabled(defaultLanguageName)) {
                process.challengeLanguage = defaultLanguage;
                process.challengeLanguagePicked = true;
              } else if (lastLanguage && langNameEnabled(lastLanguage.name)) {
                process.challengeLanguage = lastLanguage;
                process.challengeLanguagePicked = true;
              } else {
                // select the first language on the challenge that is not disabled
                process.challengeLanguage = challenge.languages.filter((l) =>
                  langNameEnabled(l.name),
                )[0];
              }

              if (!process.challengeLanguage) {
                $log.error(
                  "Invariant violation: process.challengeLanguage was expected but be defined but wasn't",
                );
                return process;
              }

              process.language = process.challengeLanguage.name;
              firebase.set(
                process.firebaseRef + '/runData/language',
                process.language,
              );
            }

            return process;
          },
        );

        //// functions:

        function beginChallenge() {
          return assessmentResultProcess
            .startAssessmentResult()
            .then(() =>
              solutionsService.create(
                challenge,
                assessmentResult,
                process.language,
              ),
            )
            .then(process.initSolution)
            .then(() => {
              firebase.set(
                process.firebaseRef + '/runData/language',
                process.language,
              );
              switchToLanguage(process.language);
            })
            .catch(appAlert.modelError);
        }

        function _updateTestFrameworkName() {
          if (process.challengeLanguage) {
            process.testFrameworkName =
              languages.frameworks[process.challengeLanguage.testFramework];
          }
          if (
            process.solution &&
            process.solution.language === process.language
          ) {
            process.testFrameworkName =
              languages.frameworks[process.solution.testFramework] ||
              process.testFrameworkName;
          }
        }

        function switchToLanguage(language) {
          return $q((resolve, reject) => {
            if (angular.isNumber(language)) {
              process.challengeLanguage = challenge.languages[language];
              language = process.challengeLanguage.name;
            } else {
              angular.forEach(challenge.languages, function (lang) {
                if (lang.name === language) {
                  process.challengeLanguage = lang;
                }
              });
            }

            process.language = language;
            challenge.setCurrentLanguage(language);
            if (process.challengeLanguage) {
              process.challengeLanguagePicked = true;
            }

            // if we have a change in solution language, reset or revert it
            if (process.solution && process.solution.language !== language) {
              let updatePromise;
              if (_updateFromLanguageHistory(process.solution, language)) {
                updatePromise = process.autosave('languageChange');
              } else {
                updatePromise = process.resetAll();
              }
              updatePromise
                .then(process.solution.files.updatePaths)
                .then(resolve, reject);
            } else {
              resolve();
            }
            _updateTestFrameworkName();

            firebase.set(process.firebaseRef + '/runData/language', language);

            _lastLanguageName = language;
          });
        }

        function reset(part = '') {
          process.disableEditors = true;
          if (assessmentResultProcess.restricted) {
            return $q.when();
          }
          return Solution[`reset${part}`](process.originalSolution, {
            data: { language: process.language },
          })
            .then(() => {
              process.fd.reset();
              _updateTestFrameworkName();
            })
            .catch(appAlert.modelError)
            .finally(() => {
              process.disableEditors = false;
            });
        }

        function submitSolution() {
          return runCode('attempt');
        }

        function runTests() {
          return runCode('test');
        }

        function setResponse(response) {
          firebase.set(process.firebaseRef + '/runData/response', response);
        }

        function runCode(type) {
          process.startRun(type);
          let solution = process.solution;

          setResponse({ result: false });

          return $q
            .all([
              // track an attempted run
              process.autosave(`init${ucFirst(type)}`).catch(() => {
                /* ignore */
              }),
              // simultaneously start running the code
              runnerFrameService
                .run({
                  language: solution.language,
                  languageVersion: process.challengeLanguage.languageVersion,
                  code: solution.code,
                  fixture:
                    type === 'test'
                      ? solution.testCases
                      : process.challengeLanguage.testCases,
                  testFramework:
                    type === 'test'
                      ? solution.testFramework
                      : process.challengeLanguage.testFramework,
                  ciphered: type === 'test' ? [] : ['fixture'],
                  setup: process.challengeLanguage.preloaded || '',
                  services: challenge.enabledServices,
                  successMode: solution.successMode || challenge.successMode,
                  relayId: solution.id,
                })
                .catch((e) => {
                  if (e && e.canceled) {
                    process.autosave(`cancel${ucFirst(type)}`);
                  } else {
                    process.autosave(`error${ucFirst(type)}`);
                  }
                  return $q.reject(e);
                }),
            ])
            .then(([, response]) => {
              appAlert.clear();
              process.runResponse = response;
              process.runResponse.type = type;
              setResponse(response);

              if (assessmentResultProcess.restricted) {
                return $q.when(response).then(process._saveSuccess);
              }

              // save results back to the server
              let run = {
                code: solution.code,
                language: solution.language,
                testCases: solution.testCases,
                response: response,
              };
              let method = type === 'test' ? 'logTest' : 'logAttempt';
              return process.originalSolution[method](run)
                .then(() => {
                  process.fd.syncUnchanged();
                  process.solution.updateScore();
                  assessmentResultProcess.refreshStageStatuses();
                  return response;
                })
                .then(process._saveSuccess, process._saveFail)
                .catch(
                  appAlert.modelError.with({
                    prefix: 'There was an error saving your results:',
                  }),
                );
            })
            .finally(process.endRun);
        }
      }
    },
  );
